import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, InboxNotification, UserNotificationsService } from '@lyricstream/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LANG_SIGNAL } from '@lyricstream/shared';
import { localizeNotification, notificationTypeLabel, timeAgo, toInternalPath, withLang } from '../../utils/notification-format';

/** Durée d'affichage du toast d'une notification reçue en direct. */
const TOAST_DURATION_MS = 6000;

/**
 * Cloche de notifications de l'utilisateur connecté : badge des non lues, dernières notifications, toast à la
 * réception en direct. N'affiche rien (et ne démarre rien) tant que l'utilisateur n'est pas connecté.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './notification-bell.html',
  styleUrl: './notification-bell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly notifications = inject(UserNotificationsService);
  private readonly translate = inject(TranslateService);
  private readonly lang = inject(LANG_SIGNAL, { optional: true });
  private readonly t = (key: string, params?: Record<string, unknown>): string => this.translate.instant(key, params);
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly menuOpen = signal(false);
  protected readonly unreadCount = this.notifications.unreadCount;
  protected readonly recent = this.notifications.recent;
  protected readonly pushed = this.notifications.pushed;
  protected readonly liveConnected = this.notifications.live;

  constructor() {
    // Le suivi (liste + push) suit la session : démarré à la connexion, arrêté et vidé à la déconnexion.
    effect(() => {
      if (this.auth.isAuthenticated()) this.notifications.start();
      else this.notifications.stop();
    });

    // Le toast d'une notification reçue en direct se ferme tout seul.
    effect(() => {
      if (!this.pushed()) return;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.notifications.dismissPushed(), TOAST_DURATION_MS);
    });

    inject(DestroyRef).onDestroy(() => {
      if (this.toastTimer) clearTimeout(this.toastTimer);
    });
  }

  protected toggleMenu(): void {
    this.menuOpen.update((v) => !v);
    if (this.menuOpen()) void this.notifications.refresh();
  }

  /** Ferme le menu au clic en dehors de la cloche. */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) this.menuOpen.set(false);
  }

  protected async markAllRead(): Promise<void> {
    try {
      await this.notifications.markAllAsRead();
    } catch {
      // La cloche se resynchronise au prochain rafraîchissement.
    }
  }

  /** Marque la notification comme lue puis ouvre sa cible (page du site ; une URL externe s'ouvre dans un nouvel onglet). */
  protected async open(item: InboxNotification): Promise<void> {
    this.menuOpen.set(false);
    this.notifications.dismissPushed();
    try {
      await this.notifications.markAsRead(item.id);
    } catch {
      // Non bloquant : on ouvre quand même la cible.
    }

    const path = toInternalPath(item.actionUrl, window.location.origin);
    if (path) {
      // Les liens sont écrits en « /fr/… » côté serveur : on garde la langue courante du visiteur.
      void this.router.navigateByUrl(withLang(path, this.currentLang()));
    } else if (item.actionUrl && /^https?:\/\//i.test(item.actionUrl)) {
      // Jamais autre chose que http(s) (pas de « javascript: »)
      window.open(item.actionUrl, '_blank', 'noopener');
    }
  }

  protected closeToast(): void {
    this.notifications.dismissPushed();
  }

  private currentLang(): string {
    return this.lang?.() ?? this.translate.currentLang ?? 'fr';
  }

  protected timeAgo(iso: string): string {
    return timeAgo(iso, this.t, this.currentLang());
  }

  protected typeLabel(type: string): string {
    return notificationTypeLabel(type, this.t);
  }

  /** Titre et message dans la langue du site (le serveur les rédige en français). */
  protected text(n: { type: string; title: string; message: string }): { title: string; message: string } {
    return localizeNotification(n, this.t);
  }
}
